import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import AdjustmentApi from '../../services/api/AdjustmentApi';
import IssueApi from '../../services/api/IssueApi';
import OrdersApi from '../../services/api/OrdersApi';
import { SORT_DIRECTION } from '../../types';
import { RootState } from '../../types';
import { showError } from '../../utils/utils';

const setRealizationPositionsLoading = createAction<{
  type: 'positions' | 'docs' | 'decisionDoc' | 'signing' | 'signingAdjustment';
  value: boolean;
}>('order-realization-detailed/setLoading');
const setSigningStatus = createAction<'success' | 'rejected'>(
  'order-realization-detailed/setSigningStatus'
);
const setError = createAction<{ value: boolean; name: 'signing' }>(
  'order-realization-detailed/setError'
);
const setSort = createAction<{
  name: 'positions' | 'sum' | 'count';
  direction: SORT_DIRECTION;
}>('order-realization-detailed/setSort');
const setAdjustmentDoc = createAction<string>(
  'order-realization-detailed/setAdjustmentDoc'
);
const setAdjustmentSort = createAction<{
  name: 'oldCount' | 'count' | 'oldSum' | 'sum';
  direction: SORT_DIRECTION;
}>('order-realization-detailed/setAdjustmentSort');
const setSigninIds = createAction<number[]>(
  'order-realization-detailed/setSigningIds'
);
const setSignedDecisionDoc = createAction<string>(
  'order-realization-detailed/setSignedDecisionDoc'
);
const setSigningInfo = createAction<{
  thumbprint: string;
  date: string;
  name: string;
}>('order-realization-detailed/setSigningInfo');

const fetchAdjustmentOrders = createAsyncThunk(
  'order-realization-detailed/fetchAdjustmentOrders',
  async (
    payload: { id: number; originalId: number; orderId: number },
    thunkAPI
  ) => {
    try {
      const store = thunkAPI.getState() as RootState;
      const { token, id: accountId } = store.auth.account;
      const originalOrderResponse = await OrdersApi.getOrderItemPositions(
        token,
        accountId,
        payload.orderId,
        payload.originalId
      );
      const orderResponse = await OrdersApi.getOrderItemPositions(
        token,
        accountId,
        payload.orderId,
        payload.id
      );
      if (
        originalOrderResponse.status === 200 &&
        orderResponse.status === 200
      ) {
        const orderChangedPositions = orderResponse.data.positions;
        const mergedArr = originalOrderResponse.data.positions.map((item) => {
          const changedPosition = orderChangedPositions.find(
            (changedItem) => changedItem.sourceRefId === item.sourceRefId
          );
          if (item.id && changedPosition) {
            return {
              oldCount: item.count,
              count: changedPosition.count,
              oldSum: item.price,
              sum: changedPosition.price,
              title: item.name,
            };
          }
          return {
            oldCount: item.count,
            count: null,
            oldSum: item.price,
            sum: null,
            title: item.name,
          };
        });

        return mergedArr;
      }

      return [];
    } catch (err) {
      showError(err, thunkAPI);
    }
  }
);

const fetchRealizationPositions = createAsyncThunk(
  'order-realization-detailed/fetchPositions',
  async (payload: { id: number; itemId: number }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setRealizationPositionsLoading({ type: 'positions', value: true })
      );
      const store = thunkAPI.getState() as RootState;
      const response = await OrdersApi.getOrderItemPositions(
        store.auth.account.token,
        store.auth.account.id,
        payload.id,
        payload.itemId
      );
      if (response.status === 200) {
        return response.data;
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setRealizationPositionsLoading({ type: 'positions', value: false })
      );
    }
  }
);

const getIssueDoc = createAsyncThunk(
  'order-realization-detailed/fetchDocs',
  async (payload: number, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setRealizationPositionsLoading({ value: true, type: 'decisionDoc' })
      );
      const state = thunkAPI.getState() as RootState;
      const response = await IssueApi.getIssueDoc(
        state.auth.account.token,
        payload
      );
      if (response) {
        return btoa(String.fromCharCode(...new Uint8Array(response.data)));
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setRealizationPositionsLoading({ value: false, type: 'decisionDoc' })
      );
    }
  }
);

const initIssuePayment = createAsyncThunk(
  'order-realization-detailed/initIssuePayment',
  async (payload: { ids: number[] }, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const infoResponse = await IssueApi.getIssueInfo(
        state.auth.account.token,
        payload.ids,
        state.auth.account.id
      );
      if (infoResponse.status === 200) {
        const decisionsIds = infoResponse.data.map(
          (item) => item.issuingDecisionId
        );
        const paymentResponse = await IssueApi.initPayment(
          state.auth.account.token,
          {
            issuingDecisionIds: decisionsIds,
            issuingInfoIds: payload.ids,
            accountId: state.auth.account.id,
          }
        );
        if (paymentResponse.status === 200) {
          return paymentResponse.data;
        }
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    }
  }
);

const initIssue = createAsyncThunk(
  'order-realization-detailed/initIssue',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const { order } = state.orderDetailed.data;
      if (order && order.initiatorId) {
        const response = await IssueApi.initIssue(state.auth.account.token, {
          accountId: state.auth.account.id,
          issues: state.realizationPositions.data.signingIds.map((item) => ({
            tokenType: 'HYBRID',
            financialClaim:
              order.items.find((oItem) => oItem.id === item)?.priceWithVat ?? 0,
            initialTokenNames: ['TMH_SIMPLE'],
            initialPurchasers: [order.recipientId],
            orderItemId: item,
          })),
        });
        if (response.status === 200) {
          thunkAPI.dispatch(getIssueDoc(response.data.id));
          return response.data;
        }
      } else {
        thunkAPI.dispatch(setError({ value: true, name: 'signing' }));
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    }
  }
);

const signDecision = createAsyncThunk(
  'order-realization-detailed/sign',
  async (_, thunkAPI) => {
    thunkAPI.dispatch(
      setRealizationPositionsLoading({ value: true, type: 'signing' })
    );
    try {
      // const store = thunkAPI.getState() as RootState;
      /*
    const response = await IssueApi.signDecision(store.auth.account.token, {...payload, accountId: store.auth.account.id})
    if(response.status === 200){
      return true
    }
    */
    } catch (err) {
      showError(err, thunkAPI);
    }
    return false;
  }
);

const initAdjustmentDoc = createAsyncThunk(
  'order-realization-detailed/initAdjustmentDoc',
  async (payload: number, thunkAPI) => {
    try {
      const store = thunkAPI.getState() as RootState;
      const response = await AdjustmentApi.initAgreementDoc(
        store.auth.account.token,
        {
          adjustmentId: payload,
          accountId: store.auth.account.id,
        }
      );
      if (response.status === 200) {
        return response.data;
      }
      return undefined;
    } catch (err) {
      showError(err, thunkAPI);
    }
  }
);

const signAdjustmentDoc = createAsyncThunk(
  'order-realization-detailed/signAdjustmentDoc',
  async (
    payload: {
      doc: Blob | string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(
        setRealizationPositionsLoading({
          value: true,
          type: 'signingAdjustment',
        })
      );
      const store = thunkAPI.getState() as RootState;
      if (store.realizationPositions.data.adjustment?.id) {
        const response = await AdjustmentApi.signAgreementDoc(
          store.auth.account.token,
          {
            adjustmentId: store.realizationPositions.data.adjustment?.id,
            doc: payload.doc,
            accountId: store.auth.account.id,
          }
        );
        if (response.status === 200) {
          return true;
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setRealizationPositionsLoading({
          value: false,
          type: 'signingAdjustment',
        })
      );
    }
    return false;
  }
);

const confirmAdjustmentDoc = createAsyncThunk(
  'order-realization-detailed/confirmAdjustmentDoc',
  async (payload: number, thunkAPI) => {
    try {
      const store = thunkAPI.getState() as RootState;
      const response = await AdjustmentApi.confirmAgreementDoc(
        store.auth.account.token,
        {
          adjustmentId: payload,
          accountId: store.auth.account.id,
        }
      );
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    }
    return undefined;
  }
);

export {
  fetchRealizationPositions,
  setRealizationPositionsLoading,
  initIssue,
  getIssueDoc,
  setSort,
  setSigninIds,
  setSignedDecisionDoc,
  signDecision,
  setSigningInfo,
  initIssuePayment,
  setAdjustmentDoc,
  setError,
  setSigningStatus,
  fetchAdjustmentOrders,
  setAdjustmentSort,
  initAdjustmentDoc,
  signAdjustmentDoc,
  confirmAdjustmentDoc,
};
